import { FC, useEffect } from "react";
import { wrapClick } from "utils";
import { useMutation, useQuery } from "@apollo/client";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import { Modal } from "components/layouts";
import { CewpUserForm } from "components/forms";
import {
  getUpdateCewpUserInitialValues,
  UpdateCewpUserSchema,
} from "components/forms/cewp-user/schema";
import { useSearch } from "react-location";
import { LocationGenerics } from "router/location";
import _ from "lodash";
import { GET_CEWP_USER, UPDATE_CEWP_USER } from "./apollo";

interface UpdateCewpUserContainerProps {
  open: boolean;
  setOpen: (val: boolean) => void;
  refetch?: () => void;
}

const UpdateCewpUserContainer: FC<UpdateCewpUserContainerProps> = ({
  open,
  setOpen,
  refetch,
}) => {
  const searchParams = useSearch<LocationGenerics>();
  const { data, loading: loadingUser } = useQuery(GET_CEWP_USER, {
    variables: {
      id: searchParams.id,
    },
    skip: !searchParams.id,
    notifyOnNetworkStatusChange: false,
  });
  const [updateCEWPUser, { loading }] = useMutation(UPDATE_CEWP_USER);

  const form = useFormik({
    initialValues: getUpdateCewpUserInitialValues(),
    validationSchema: UpdateCewpUserSchema,
    onSubmit: async (values) => {
      const districts = values.districts.filter(Boolean);
      await updateCEWPUser({
        variables: {
          id: searchParams.id,
          firstName: values.firstName,
          lastName: values.lastName,
          phoneNumber: values.phoneNumber,
          emailAddress: values.emailAddress,
          certificationNumber: values.certificationNumber,
          profileImageUrl: values.profileImageUrl || undefined,
          districts: districts.length ? districts : undefined,
        },
      }).then(({ data: result }) => {
        if (result?.updateCEWPUser?._id) {
          toast(
            JSON.stringify({
              type: "success",
              title: "CEWP User Updated Successfully",
            }),
          );
          refetch?.();
          setOpen(false);
        } else {
          toast(
            JSON.stringify({
              type: "error",
              title: "Could not update CEWP user",
            }),
          );
        }
      });
    },
    onReset: () => {
      setOpen(false);
    },
  });

  useEffect(() => {
    if (!data?.user) return;
    const districtIds = _.map(data.user.districts, "_id") as string[];
    form.setValues({
      firstName: data.user.firstName || "",
      lastName: data.user.lastName || "",
      phoneNumber: data.user.phoneNumber || "",
      emailAddress: data.user.emailAddress || "",
      certificationNumber: data.user.certificationNumber || "",
      profileImageUrl: data.user.profileImageUrl || "",
      districts: districtIds.length ? districtIds : [""],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.user, searchParams.id, open]);

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      loading={loadingUser}
      title="Edit CEWP User"
      description="Provide the details to update this CEWP user"
      renderActions={() => (
        <button
          type="button"
          disabled={loading}
          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
          onClick={wrapClick(form.handleSubmit)}
        >
          {loading ? "Saving..." : "Save Changes"}
        </button>
      )}
    >
      {data?.user?._id && <CewpUserForm form={form} />}
    </Modal>
  );
};

export default UpdateCewpUserContainer;
